/**
 * ONE line of governed names — From / To / Cc on a mail row, Organizer / Attendees on a meeting row
 * (`docs/specs/strategic-architecture/timeline-inline-message-spec.md` §3.1, §6).
 *
 * PRESENTATIONAL ONLY. The link rule is the SERVER'S and lives in exactly one place —
 * `CrmParticipants.ParticipantName` / `isLinkable`: a name links when the server resolved it to a
 * person AND did not flag `profile_hidden`, and renders as plain text otherwise. Nothing here
 * re-derives that, and no domain test is reinvented (that is how a gate drifts).
 *
 * ⛔ THE LINE CLIPS, THE CONTROL DOES NOT. The names sit in a `nowrap; overflow: hidden` span and
 * **Show more** is that span's SIBLING (spec §5.4). A control rendered inside the clip can clip
 * itself away, which on a governed surface reads as "something is being withheld" rather than
 * "the line is long".
 *
 * ⛔ THE CONTROL IS MEASURED, NEVER ASSUMED — `ClampedBlock`'s discipline, one axis over: the span
 * overflows when `scrollWidth > clientWidth`, re-taken on resize. And the measurement is SKIPPED
 * while expanded, because a wrapped line reports no horizontal overflow, would conclude "it fits",
 * and would delete the only control that collapses it again (the failure `ClampedBlock`'s own tests
 * pin on its axis).
 *
 * ⚠ Consequence in jsdom, stated so nobody debugs it twice: layout is not computed there, so both
 * widths read 0 and NO control renders unless a test stubs them (the `ClampedBlock.test.tsx`
 * pattern). That is the right default — tests that don't care about clipping see the whole line.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { ParticipantName, type OpenPerson, type ParticipantLike }
  from '../crm/CrmParticipants';
import styles from './ParticipantLine.module.css';

export interface ParticipantGroup {
  /** "From" / "To" / "Cc" / "Organizer" / "Attendees" — rendered as the line's inline label. */
  label: string;
  people: ParticipantLike[];
}

export interface ParticipantLineProps {
  groups: ParticipantGroup[];
  /** In-place profile navigation. Omitted → linkable names still render, without the click handler. */
  openPerson?: OpenPerson;
  /** The host surface's own line style (indent, type, colour). */
  className?: string;
}

export function ParticipantLine({ groups, openPerson, className }: ParticipantLineProps) {
  const clip = useRef<HTMLSpanElement | null>(null);
  const names = useRef<HTMLSpanElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  const measure = useCallback(() => {
    const box = clip.current;
    const content = names.current;
    if (!box || !content || expanded) return;   // see the ⛔ above: a wrapped line always "fits"
    setOverflows(content.scrollWidth > box.clientWidth);
  }, [expanded]);

  useEffect(() => {
    measure();
    // ⛔ OBSERVE BOTH ELEMENTS (review F2). Two different changes flip the answer, and each one is
    // invisible on the other's element:
    //   * the CONTENT gets wider — a web font swaps in after mount and the same names take 15%
    //     more room. The box's width never moves, so watching only the box leaves the line
    //     silently ellipsized with no Show more at all. That was the defect.
    //   * the BOX gets narrower — a window resize. The content's own width never moves, so
    //     watching only the content loses the resize half.
    // `document.fonts.ready` is the belt for the font swap on browsers whose ResizeObserver fires
    // before the re-layout settles; guarded, because jsdom has no FontFaceSet.
    let cancelled = false;
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (fonts?.ready) void fonts.ready.then(() => { if (!cancelled) measure(); }).catch(() => {});
    if (typeof ResizeObserver === 'undefined') return () => { cancelled = true; };
    const content = names.current;
    const box = clip.current;
    if (!content || !box) return () => { cancelled = true; };
    const ro = new ResizeObserver(() => measure());
    ro.observe(content);
    ro.observe(box);
    return () => { cancelled = true; ro.disconnect(); };
  }, [measure, groups]);

  const filled = groups.filter((gr) => gr.people.length > 0);
  if (filled.length === 0) return null;

  return (
    // ⛔ THE COMPONENT OWNS ITS OWN ROW LAYOUT (review F1). It used to borrow the host's — the
    // timeline's `.tlParties`, which WRAPS — so an overflowing names span took the whole first line
    // and pushed "Show more" onto a second: a three-line row, where the spec asks for one line with
    // the control at its end. A host that is not a flex row at all would have got no clip whatsoever.
    <div className={className ? `${styles.line} ${className}` : styles.line}>
      <span
        ref={clip}
        className={expanded ? styles.open : styles.clip}
        data-participant-line={expanded ? 'open' : 'clipped'}
      >
        {/* The MEASURED element: never clipped itself, so its `scrollWidth` is the true content
            width even while the box around it is hiding the overflow. */}
        <span
          ref={names}
          className={expanded ? styles.namesOpen : styles.names}
          data-participant-names={expanded ? 'open' : 'clipped'}
        >
          {filled.map((gr) => (
            <span key={gr.label} className={styles.group}>
              <span className={styles.label}>{gr.label}</span>
              {gr.people.map((p, i) => (
                <span key={`${p.resolved_person_id ?? p.email_address ?? i}-${i}`}>
                  {i > 0 && ', '}
                  <ParticipantName p={p} openPerson={openPerson} />
                </span>
              ))}
            </span>
          ))}
        </span>
      </span>
      {overflows && (
        <button
          type="button"
          className={styles.toggle}
          data-participant-toggle=""
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

export default ParticipantLine;
